import { Icon, TableRowWithExpander, ThresholdPill } from '@aon/aon-tws-common-ui';
import { DimensionType } from '@aon/tws-common-ui-questions/build/lib/models/enum';
import React from 'react';
import styled from 'styled-components';
import customFlag from '../../../assets/images/icon-customFlag.png';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import { IColumn, ISurveyDimension, ISurveyQuestionEditModel } from '../../../models';
import { getUniqueDimensionKey } from '../../../utilities/DimensionHelper';
import { useSurveyDefaultCultureCode } from '../../../utilities/hooks';
import { useManageSurveyDimensionColumns } from '../../../utilities/hooks/useManageSurveyDimensionsColumns';
import { ErrorMessageLabelNoIcon } from '../../audience/SharedComponents';
import DimensionQuestions from './DimensionQuestions';

export interface IManageSurveyDimensionsTableRowProps {
	dimension: ISurveyDimension;
	selectedColumns: IColumn[];
	isExpanded: boolean;
	onToggleIsExpanded: (dimension: ISurveyDimension, isExpanded: boolean) => void;
	selectedQuestions: ISurveyQuestionEditModel[];
	onToggleSingleQuestionSelection: (dimension: ISurveyDimension, question: ISurveyQuestionEditModel, isSelected: boolean) => void;
	onToggleAllQuestionsSelection: (dimension: ISurveyDimension, questions: ISurveyQuestionEditModel[], isSelected: boolean) => void;
	onEdit: (dimension: ISurveyDimension) => void;
	onDelete: (dimension: ISurveyDimension) => void;
	onMoveQuestions: (sourceDimensions: ISurveyDimension, questionsToMove: ISurveyQuestionEditModel[]) => void;
	onCopyQuestions: (sourceDimensions: ISurveyDimension, questionsToCopy: ISurveyQuestionEditModel[]) => void;
	onRemoveQuestions: (sourceDimension: ISurveyDimension, questionsToRemove: ISurveyQuestionEditModel[]) => void;
}

const IconWrapper = styled.span`
	cursor: pointer;
`;

const CustomTableRowWithExpander = styled(TableRowWithExpander)`
	td.name {
		position: relative;

		& img {
			height: 40px;
			width: auto;
			position: absolute;
			top: 0;
			left: 0;
		}
	}
`;

const ManageSurveyDimensionsTableRow = ({
	dimension,
	selectedColumns,
	isExpanded,
	onToggleIsExpanded,
	selectedQuestions,
	onToggleAllQuestionsSelection,
	onToggleSingleQuestionSelection,
	onEdit,
	onMoveQuestions,
	onCopyQuestions,
	onRemoveQuestions,
	onDelete,
}: IManageSurveyDimensionsTableRowProps) => {
	const { nameColumn, questionsColumn, sourceColumn, typeColumn, responseOptionColumn, scoredColumn, actionsColumn } =
		useManageSurveyDimensionColumns();
	const defaultCultureCode = useSurveyDefaultCultureCode();
	const visibleColumns = selectedColumns.filter((column) => column.checked);

	const getDimensionSource = () => {
		if (dimension.ownerId === undefined) {
			return '--';
		} else if (dimension.ownerId === 0) {
			return 'Custom';
		} else {
			return dimension.ownerId == Constants.KINCENTRIC_OWNER_ID ? 'Kincentric Library' : 'My Company Library';
		}
	};

	const getDimensionType = () => {
		switch (dimension.dimensionType) {
			case DimensionType.Dimension:
				return 'Dimension';
			case DimensionType.Index:
				return 'Index';
			default:
				return '--';
		}
	};

	const getDimensionResponseOption = () => {
		return dimension.responseOptionGroupName;
	};

	const getDimensionName = () => {
		const translation = dimension.translations?.find((t) => t.cultureCode === defaultCultureCode);
		const name = (
			<>
				{dimension.name}
				{getDimensionSource() === 'Custom' && <img src={customFlag} />}
			</>
		);
		if (
			dimension.name !== ContentConstants.DIMENSION_CATEGORIES_DEMOGRAPHIC &&
			dimension.name !== ContentConstants.DIMENSION_CATEGORIES_NO_DIMENSIONS &&
			!translation?.textValue
		) {
			return (
				<>
					{name}
					<ErrorMessageLabelNoIcon>{ContentConstants.WARNING_MISSING_DEFAULT_TRANSLATION}</ErrorMessageLabelNoIcon>
				</>
			);
		}
		return name;
	};

	const getDimensionColumnContent = (column: IColumn) => {
		switch (column.name) {
			case nameColumn.name:
				return getDimensionName();
			case questionsColumn.name:
				return <ThresholdPill count={dimension.surveyQuestions?.length ?? 0} />;
			case sourceColumn.name:
				return getDimensionSource();
			case typeColumn.name:
				return getDimensionType();
			case responseOptionColumn.name:
				return getDimensionResponseOption();
			case scoredColumn.name:
				return dimension.isScored ? <Icon name='yes' aria-label='yes' /> : '--';
			case actionsColumn.name:
				return (
					<>
						{!dimension.shouldDisableEdit && (
							<IconWrapper>
								<Icon name='edit' onClick={() => onEdit(dimension)} aria-label='edit'></Icon>
							</IconWrapper>
						)}
						{!dimension.shouldDisableDelete && (
							<IconWrapper>
								<Icon name='delete' onClick={() => onDelete(dimension)} aria-label='delete' className='pl-2' />
							</IconWrapper>
						)}
					</>
				);
			default:
				return null;
		}
	};

	const getExpandedRow = () => {
		return (
			<td colSpan={selectedColumns.length + 1}>
				<DimensionQuestions
					key={getUniqueDimensionKey(dimension)}
					questions={dimension.surveyQuestions ?? []}
					dimension={dimension}
					onMoveQuestions={onMoveQuestions}
					onCopyQuestions={onCopyQuestions}
					onRemoveQuestions={onRemoveQuestions}
					selectedQuestions={selectedQuestions}
					onToggleSingleQuestionSelection={onToggleSingleQuestionSelection}
					onToggleAllQuestionsSelection={onToggleAllQuestionsSelection}
				/>
			</td>
		);
	};

	return (
		<CustomTableRowWithExpander
			expanded={isExpanded}
			onExpanderClick={() => onToggleIsExpanded(dimension, !isExpanded)}
			expandedRowRender={getExpandedRow}
		>
			{visibleColumns.map((column) => (
				<td className={column.name === nameColumn.name ? 'name' : undefined} key={`${getUniqueDimensionKey(dimension)}-${column.name}`}>
					{getDimensionColumnContent(column)}
				</td>
			))}
		</CustomTableRowWithExpander>
	);
};

export default ManageSurveyDimensionsTableRow;
